import { DARK_MODE, GRAY_DARK, GRAY, GRAY_LIGHT, BORDER, GRAY_SCALE } from 'constants/ThemeConstant';
import { resolveThemeConfig, getThemeLayoutBodyBg, getCssThemeMode } from 'theme/themes';

const NATIONAL_PRIMARY = '#9F0000';
const NATIONAL_GOLD = '#FFD700';
const NATIONAL_GOLD_SOFT = '#FFC466';

const hexToRgba = (hex, alpha) => {
    const normalized = String(hex || '').replace('#', '');
    if (normalized.length !== 6) {
        return `rgba(0, 0, 0, ${alpha})`;
    }
    const r = parseInt(normalized.slice(0, 2), 16);
    const g = parseInt(normalized.slice(2, 4), 16);
    const b = parseInt(normalized.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const buildKpiIcons = (primary, accent, secondaryAccent) => ({
    total: { bg: hexToRgba(primary, 0.12), color: primary },
    inUse: { bg: hexToRgba(accent, 0.2), color: accent },
    idle: { bg: hexToRgba(secondaryAccent, 0.25), color: secondaryAccent },
    repair: { bg: hexToRgba(primary, 0.12), color: primary },
});

const LIGHT_DEFAULT = {
    mode: 'light',
    cardBg: '#ffffff',
    cardBorder: BORDER.BASE_COLOR,
    rowBg: 'rgba(0, 0, 0, 0.025)',
    textPrimary: GRAY_DARK,
    textMuted: GRAY_LIGHT,
    chartForeColor: GRAY,
    gridBorder: BORDER.BASE_COLOR,
    accentPrimary: '#3e82f7',
    chartBarColor: '#38bdf8',
    statValueColor: null,
    chartPalette: ['#34d399', '#facc15', '#fb923c', '#f87171'],
    statusColors: {
        inUse: '#34d399',
        idle: '#facc15',
        repair: '#fb923c',
        broken: '#f87171',
    },
    kpi: buildKpiIcons('#7c9cff', '#2dd4bf', '#facc15'),
    panelIcon: { bg: 'rgba(90, 140, 255, 0.18)', color: '#7c9cff' },
    alertIcon: { bg: 'rgba(234, 179, 8, 0.18)', color: '#facc15' },
    heatmapPalette: ['#1a1f35', '#4c3d8f', '#7c3aed', '#6366f1', '#93c5fd'],
    semanticChart: {
        positive: '#34d399',
        negative: '#f87171',
        seriesA: '#60a5fa',
        seriesB: '#34d399',
    },
    queuePalette: ['#60a5fa', '#facc15', '#a78bfa', '#34d399'],
};

const DARK_DEFAULT = {
    mode: 'dark',
    cardBg: DARK_MODE.BG_COLOR,
    cardBorder: DARK_MODE.BORDER_BASE_COLOR,
    rowBg: 'rgba(255, 255, 255, 0.04)',
    textPrimary: DARK_MODE.HEADING_COLOR,
    textMuted: DARK_MODE.TEXT_COLOR,
    chartForeColor: DARK_MODE.TEXT_COLOR,
    gridBorder: DARK_MODE.BORDER_BASE_COLOR,
    // accentPrimary: '#0052AD',
    accentPrimary: DARK_MODE.PRIMARY_COLOR,
    chartBarColor: '#38bdf8',
    statValueColor: null,
    chartPalette: ['#34d399', '#facc15', '#fb923c', '#f87171'],
    statusColors: {
        inUse: '#34d399',
        idle: '#facc15',
        repair: '#fb923c',
        broken: '#f87171',
    },
    kpi: buildKpiIcons('#7c9cff', '#2dd4bf', '#facc15'),
    panelIcon: { bg: 'rgba(90, 140, 255, 0.18)', color: '#7c9cff' },
    alertIcon: { bg: 'rgba(234, 179, 8, 0.18)', color: '#facc15' },
    heatmapPalette: ['#1a1f35', '#4c3d8f', '#7c3aed', '#6366f1', '#93c5fd'],
    semanticChart: {
        positive: '#34d399',
        negative: '#f87171',
        seriesA: '#60a5fa',
        seriesB: '#34d399',
    },
    queuePalette: ['#60a5fa', '#facc15', '#a78bfa', '#34d399'],
};

export const buildDashboardThemeTokens = (themeKey) => {
    const config = resolveThemeConfig(themeKey);
    const token = config?.token ?? {};
    const cssMode = getCssThemeMode(themeKey);
    const pageBg = getThemeLayoutBodyBg(themeKey, token) ?? token.colorBgLayout ?? token.bodyBg ?? '#f0f2f5';
    const primary = token.colorPrimary ?? LIGHT_DEFAULT.accentPrimary;

    if (cssMode === 'dark') {
        return {
            ...DARK_DEFAULT,
            themeKey,
            pageBg,
        };
    }

    if (themeKey === 'nationalDay') {
        return {
            mode: 'light',
            themeKey,
            pageBg: pageBg ?? '#FFF8ED',
            cardBg: '#ffffff',
            cardBorder: token.colorBorder ?? '#E8DCC8',
            rowBg: 'rgba(159, 0, 0, 0.04)',
            textPrimary: NATIONAL_PRIMARY,
            textMuted: '#72849a',
            chartForeColor: GRAY,
            gridBorder: token.colorBorder ?? '#E8DCC8',
            accentPrimary: NATIONAL_PRIMARY,
            chartBarColor: NATIONAL_PRIMARY,
            statValueColor: NATIONAL_PRIMARY,
            chartPalette: [NATIONAL_PRIMARY, NATIONAL_GOLD, NATIONAL_GOLD_SOFT, '#C41E3A'],
            statusColors: {
                inUse: NATIONAL_GOLD,
                idle: NATIONAL_GOLD_SOFT,
                repair: '#FA8C16',
                broken: NATIONAL_PRIMARY,
            },
            kpi: buildKpiIcons(NATIONAL_PRIMARY, NATIONAL_GOLD, NATIONAL_GOLD_SOFT),
            panelIcon: { bg: hexToRgba(NATIONAL_PRIMARY, 0.12), color: NATIONAL_PRIMARY },
            alertIcon: { bg: hexToRgba(NATIONAL_GOLD, 0.2), color: NATIONAL_GOLD },
            heatmapPalette: ['#2d0505', '#5c0000', '#7a0000', '#9F0000', '#FFD700'],
            semanticChart: {
                positive: NATIONAL_GOLD,
                negative: NATIONAL_PRIMARY,
                seriesA: NATIONAL_PRIMARY,
                seriesB: NATIONAL_GOLD,
            },
            queuePalette: [NATIONAL_PRIMARY, NATIONAL_GOLD, NATIONAL_GOLD_SOFT, '#C41E3A'],
        };
    }

    const isFestive = themeKey !== 'light';
    const festiveTokens = isFestive
        ? {
              textPrimary: primary,
              accentPrimary: primary,
              chartBarColor: primary,
              statValueColor: primary,
              chartPalette: [primary, NATIONAL_GOLD, NATIONAL_GOLD_SOFT, '#FA8C16'],
              statusColors: {
                  inUse: primary,
                  idle: NATIONAL_GOLD,
                  repair: '#FA8C16',
                  broken: '#FF4D4F',
              },
              kpi: buildKpiIcons(primary, NATIONAL_GOLD, NATIONAL_GOLD_SOFT),
              panelIcon: { bg: hexToRgba(primary, 0.12), color: primary },
              alertIcon: { bg: hexToRgba(NATIONAL_GOLD, 0.2), color: NATIONAL_GOLD },
              heatmapPalette: [
                  hexToRgba(primary, 1),
                  hexToRgba(primary, 0.75),
                  hexToRgba(primary, 0.55),
                  NATIONAL_GOLD_SOFT,
                  NATIONAL_GOLD,
              ],
              semanticChart: {
                  positive: NATIONAL_GOLD,
                  negative: primary,
                  seriesA: primary,
                  seriesB: NATIONAL_GOLD,
              },
              queuePalette: [primary, NATIONAL_GOLD, NATIONAL_GOLD_SOFT, '#C41E3A'],
          }
        : {};

    return {
        ...LIGHT_DEFAULT,
        themeKey,
        pageBg,
        cardBg: token.colorBgElevated ?? LIGHT_DEFAULT.cardBg,
        cardBorder: token.colorBorder ?? LIGHT_DEFAULT.cardBorder,
        ...festiveTokens,
    };
};

/** Viền card phân hệ dashboard — accent top + viền xung quanh (dark: cardBorder từ theme) */
export const getModuleCardShellStyle = (dashboardTheme) => ({
    '--module-card-accent': dashboardTheme.accentPrimary,
    '--module-card-side-border': dashboardTheme.cardBorder,
});

/** Nút «Mở phân hệ» trên module card — hover/active khớp accent theme (dark: chữ trắng + nền accent khi bấm) */
export const getModuleCardOpenButtonPresentation = (dashboardTheme) => {
    const accent = dashboardTheme.accentPrimary;
    const isDark = dashboardTheme.mode === 'dark';
    const labelColor = isDark ? GRAY_SCALE.WHITE : accent;

    return {
        className: isDark ? 'module-card-open-module-btn module-card-open-module-btn--dark' : 'module-card-open-module-btn',
        style: {
            '--module-card-open-btn-accent': accent,
            '--module-card-open-btn-label': labelColor,
            color: labelColor,
            borderColor: accent,
            backgroundColor: 'transparent',
            boxShadow: 'none',
        },
    };
};

export default buildDashboardThemeTokens;
